import React, { useEffect, useRef, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  Row,
  Col,
  Typography,
  Space,
  message,
  Checkbox,
  Switch,
  Table,
  Popconfirm,
  Alert,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import api from '../config/api';
import AddressAutocompleteInput from '../components/AddressAutocompleteInput';
import {
  businessCountryFormToApi,
  googleCountryToBusinessForm,
  normalizeBusinessCountryFromDb,
  normalizeStateForSubmit,
  shouldUseStateSelect,
} from '../utils/addressUtils';
import { findStateCodeByLabel, getStatesByCountry } from '../data/locationOptions';

const { Title } = Typography;
const { Option } = Select;

/** Keeps Select dropdowns above Google address suggestion overlays */
const FORM_SELECT_DROPDOWN_STYLE = { zIndex: 2100 };

function formatSalesAgentOptionLabel(agent: {
  first_name?: string;
  last_name?: string;
  company_names?: string | null;
  role?: string;
  role_display_name?: string | null;
}): string {
  const name = [agent.first_name, agent.last_name].filter(Boolean).join(' ').trim() || 'Unknown';
  const role =
    agent.role === 'sales_rep_associated'
      ? 'Sales Rep - Associated'
      : agent.role_display_name ||
        (agent.role
          ? agent.role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
          : 'Sales Agent');
  const company = agent.company_names?.trim();
  if (company) {
    return `${name} — ${company} (${role})`;
  }
  return `${name} (${role})`;
}

interface Company {
  id: string;
  name: string;
  is_active: boolean;
}

// Available equipment types
const EQUIPMENT_TYPES = [
  { value: 'pos_terminal', label: 'POS Terminal' },

];

const CreateBusiness: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canAddEditBusiness = usePermission('add_edit_business');
  const [form] = Form.useForm();
  const isEdit = Boolean(id);
  const selectedCompanyId = Form.useWatch('company_id', form);

  useEffect(() => {
    if (!canAddEditBusiness) {
      message.warning(isEdit ? 'You do not have permission to edit businesses.' : 'You do not have permission to add businesses.');
      navigate(isEdit && id ? `/businesses/${id}` : '/businesses');
    }
  }, [isEdit, canAddEditBusiness, navigate, id]);
  
  // Equipment assignment state
  const [equipmentToAssign, setEquipmentToAssign] = useState<any[]>([]);
  const [equipmentForm] = Form.useForm();

  // Fetch business data for editing (needed before company-scoped sales agent list)
  const { data: business, isLoading, error: businessError } = useQuery(
    ['business', id],
    async () => {
      const response = await api.get(`/api/businesses/${id}`);
      return response.data;
    },
    {
      enabled: isEdit,
    }
  );

  // Sales agents: all when no company selected; filtered when company is chosen
  const { data: salesAgents = [], error: salesAgentsError } = useQuery(
    ['sales-agents-by-company', selectedCompanyId ?? 'all'],
    async () => {
      const response = await api.get('/api/users/sales-agents', {
        params: selectedCompanyId ? { company_id: selectedCompanyId } : {},
      });
      return response.data || [];
    }
  );

  const prevCompanyIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (prevCompanyIdRef.current !== undefined && prevCompanyIdRef.current !== selectedCompanyId) {
      if (selectedCompanyId) {
        form.setFieldValue('sales_agent_ids', []);
      }
    }
    prevCompanyIdRef.current = selectedCompanyId;
  }, [selectedCompanyId, form]);

  useEffect(() => {
    if (!selectedCompanyId) return;
    const currentIds: string[] = form.getFieldValue('sales_agent_ids') || [];
    if (currentIds.length === 0) return;
    const allowed = new Set((salesAgents as any[]).map((a) => a.id));
    const filtered = currentIds.filter((id) => allowed.has(id));
    if (filtered.length !== currentIds.length) {
      form.setFieldValue('sales_agent_ids', filtered);
    }
  }, [salesAgents, selectedCompanyId, form]);

  // Fetch assignable users (agent, developer, admin) for default follower
  const { data: assignableUsers = [] } = useQuery('users-assignable', async () => {
    const response = await api.get('/api/users/assignable');
    return response.data || [];
  }, { staleTime: 5 * 60 * 1000 });

  // Fetch business types
  const { data: businessTypes = [] } = useQuery('business-types', async () => {
    const response = await api.get('/api/business-types');
    return response.data?.businessTypes || [];
  });

  // Fetch companies for assignment
  const { data: companies = [] } = useQuery('companies-for-business-form', async () => {
    const response = await api.get('/api/companies');
    return (response.data?.companies || []) as Company[];
  }, {
    enabled: user?.role === 'admin',
  });

  // Fetch services (admin-managed list) for the Services checkbox group
  const { data: servicesList = [] } = useQuery('services', async () => {
    const response = await api.get('/api/services');
    return response.data?.services || [];
  });
  const serviceOptions = (servicesList as any[]).map((s: any) => ({
    value: s.name,
    label: (s.name || '').replace(/_/g, ' '),
  }));

  // Fetch business services for editing
  const { data: businessServices = [] } = useQuery(
    ['business-services', id],
    async () => {
      const response = await api.get(`/api/businesses/${id}/services`);
      return response.data?.services || [];
    },
    {
      enabled: isEdit,
    }
  );

  // Fetch business sales agents for editing
  const { data: businessSalesAgents = [] } = useQuery(
    ['business-sales-agents', id],
    async () => {
      const response = await api.get(`/api/businesses/${id}/sales-agents`);
      return response.data?.salesAgents || [];
    },
    {
      enabled: isEdit,
    }
  );

  // Fetch available equipment for assignment
  const { data: availableEquipment = [] } = useQuery(
    'available-equipment',
    async () => {
      const response = await api.get('/api/equipment');
      // Handle the API response structure: { equipment: [...], total: 3, page: 1, limit: 20 }
      return response.data?.equipment || response.data || [];
    }
  );

  // Debug logging

  // Create/Update business mutation
  const createBusiness = useMutation(
    async (data: any) => {
      const { services, equipmentToAssign, ...businessData } = data;
      
      let response;
      if (isEdit) {
        response = await api.put(`/api/businesses/${id}`, businessData);
      } else {
        response = await api.post('/api/businesses', businessData);
      }
      
      // Handle services separately
      if (services && services.length > 0) {
        const businessId = response.data.id;
        
        // For editing, first remove all existing services
        if (isEdit) {
          const existingServices = businessServices.map((s: any) => s.service_type);
          
          for (const serviceType of existingServices) {
            try {
              await api.delete(`/api/businesses/${businessId}/services/${serviceType}`);
            } catch (error: any) {
              console.error(`Failed to remove service ${serviceType}:`, error);
              throw new Error(`Failed to remove service ${serviceType}: ${error.response?.data?.error || error.message}`);
            }
          }
        }
        
        // Add selected services
        for (const serviceType of services) {
          try {
            await api.post(`/api/businesses/${businessId}/services`, { service_type: serviceType });
          } catch (error: any) {
            console.error(`Failed to add service ${serviceType}:`, error);
            throw new Error(`Failed to add service ${serviceType}: ${error.response?.data?.error || error.message}`);
          }
        }
      }
      
      // Handle equipment assignment
      if (equipmentToAssign && equipmentToAssign.length > 0) {
        const businessId = response.data.id;
        
        for (const assignment of equipmentToAssign) {
          try {
            await api.post(`/api/equipment/${assignment.equipment_id}/assign`, {
              business_id: businessId,
              location: assignment.location,
              notes: assignment.notes,
            });
          } catch (error: any) {
            console.warn(`Failed to assign equipment ${assignment.equipment_id}:`, error);
          }
        }
      }
      
      return response;
    },
    {
      onSuccess: (response) => {
        const action = isEdit ? 'updated' : 'created';
        message.success(`Business ${action} successfully`);
        queryClient.invalidateQueries('businesses');
        queryClient.invalidateQueries('equipment');
        navigate(`/businesses/${response.data.id}`);
      },
      onError: (error: any) => {
        const action = isEdit ? 'update' : 'create';
        message.error(error.response?.data?.error || `Failed to ${action} business`);
      }
    }
  );

  // Populate form when editing
  useEffect(() => {
    if (business && isEdit) {
      const formData = { ...business };

      const { country, country_custom } = normalizeBusinessCountryFromDb(business.country);
      formData.country = country;
      formData.country_custom = country_custom;
      const cc = country === 'USA' ? 'US' : country === 'Canada' ? 'CA' : null;
      if (cc && business.state) {
        formData.state = findStateCodeByLabel(business.state, cc);
      }

      // Set services from businessServices
      if (businessServices && businessServices.length > 0) {
        formData.services = businessServices.map((s: any) => s.service_type);
      }
      
      // Set sales agents from businessSalesAgents
      if (businessSalesAgents && businessSalesAgents.length > 0) {
        formData.sales_agent_ids = businessSalesAgents.map((agent: any) => agent.id);
      }

      if (business.default_followers) {
        formData.default_follower_user_ids = (business.default_followers.users || []).map((u: any) => u.id);
        formData.default_follower_emails = business.default_followers.emails || [];
      }
      
      form.setFieldsValue(formData);
    }
  }, [business, businessServices, businessSalesAgents, isEdit, form]);

  // Equipment assignment functions
  const addEquipment = (values: any) => {
    const selectedEquipment = Array.isArray(availableEquipment) ? availableEquipment.find((eq: any) => eq.id === values.equipment_id) : null;
    if (selectedEquipment) {
      const newAssignment = {
        id: Date.now(), // Temporary ID for the list
        equipment_id: values.equipment_id,
        equipment_type: selectedEquipment.equipment_type,
        model: selectedEquipment.model,
        location: values.location,
        notes: values.notes,
        quantity: values.quantity || 1,
      };
      setEquipmentToAssign([...equipmentToAssign, newAssignment]);
      equipmentForm.resetFields();
    }
  };

  const removeEquipment = (index: number) => {
    const newList = equipmentToAssign.filter((_, i) => i !== index);
    setEquipmentToAssign(newList);
  };

  const handleSubmit = (values: any) => {
    const { services, equipmentToAssign, country_custom, ...rest } = values;
    const country = businessCountryFormToApi(values.country, country_custom);
    const state = normalizeStateForSubmit(values.state, values.country);
    createBusiness.mutate({
      ...rest,
      country,
      state,
      services,
      equipmentToAssign,
    });
  };

  if (isEdit && isLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <div>Loading business data...</div>
      </div>
    );
  }

  if (isEdit && businessError) {
    return (
      <div style={{ padding: 24 }}>
        <div>Error loading business: {(businessError as any)?.message || 'Unknown error'}</div>
        <Button onClick={() => navigate('/businesses')}>Back to Businesses</Button>
      </div>
    );
  }

  // Check if user is authenticated
  if (!user) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Alert message="Please log in to access this page" type="warning" />
      </div>
    );
  }

  // Check for sales agents error
  if (salesAgentsError) {
    return (
      <div style={{ padding: 24 }}>
        <Alert 
          message="Error loading sales agents" 
          description={(salesAgentsError as any)?.message || 'Failed to load sales agents'} 
          type="error" 
        />
        <Button onClick={() => navigate('/businesses')} style={{ marginTop: 16 }}>
          Back to Businesses
        </Button>
      </div>
    );
  }

  try {
    return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: 'calc(100vh - 64px)' }}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/businesses')}
        style={{ marginBottom: 16 }}
      >
        Back to Businesses
      </Button>

      <Card>
        <Title level={2}>{isEdit ? 'Edit Business' : 'Create New Business'}</Title>
        
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            country: 'USA',
            country_custom: '',
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="business_name"
                label="Business Name"
                rules={[{ required: true, message: 'Please enter business name' }]}
              >
                <Input placeholder="Enter business name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="mid"
                label="MID"
                rules={[{ required: false }]}
              >
                <Input placeholder="Enter MID (optional)" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="store_id"
                label="Store ID"
                rules={[
                  { max: 255, message: 'Store ID must be 255 characters or less' },
                  {
                    validator: async (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }
                      // Check uniqueness only if editing and value changed
                      if (isEdit && business && business.store_id === value) {
                        return Promise.resolve();
                      }
                      // Frontend validation - backend will also check
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Input placeholder="Enter unique Store ID (optional)" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="contact_person"
                label="Contact Person"
              >
                <Input placeholder="Enter contact person name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Phone"
              >
                <Input placeholder="Enter phone number" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="cell_phone"
                label="Cell Phone"
              >
                <Input placeholder="Enter cell phone number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="other_number"
                label="Other Number"
              >
                <Input placeholder="Enter other phone number" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[{ type: 'email', message: 'Please enter a valid email' }]}
              >
                <Input placeholder="Enter email address" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="business_type_id"
                label="Business Type"
              >
                <Select placeholder="Select business type" allowClear>
                  <Option value="">None</Option>
                  {businessTypes.map((type: any) => (
                    <Option key={type.id} value={type.id}>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {user?.role === 'admin' && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="company_id"
                  label="Company"
                >
                  <Select
                    placeholder="Select company (optional)"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    options={(companies as Company[])
                      .filter((c) => c.is_active)
                      .map((c) => ({ value: c.id, label: c.name }))}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="sales_agent_ids"
                label="Sales Agents"
              >
                <Select 
                  mode="multiple" 
                  placeholder={selectedCompanyId ? 'Select sales agents for this company' : 'Select sales agents'}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  dropdownStyle={FORM_SELECT_DROPDOWN_STYLE}
                  options={(salesAgents as any[]).map((agent: any) => ({
                    value: agent.id,
                    label: formatSalesAgentOptionLabel(agent),
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="default_follower_user_ids"
                label="Internal User"
              >
                <Select
                  mode="multiple"
                  placeholder="Agent, Developer, or Admin"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  dropdownStyle={FORM_SELECT_DROPDOWN_STYLE}
                >
                  {(assignableUsers as any[]).map((u: any) => (
                    <Option key={u.id} value={u.id}>
                      {u.first_name} {u.last_name} ({u.role || u.role_display_name || 'user'})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="default_follower_emails"
                label="Email only"
                rules={[
                  {
                    type: 'array',
                    message: 'Enter valid emails',
                    validator: (_, val) => {
                      if (!val || val.length === 0) return Promise.resolve();
                      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                      const bad = (val as string[]).filter((e: string) => !re.test(String(e).trim()));
                      return bad.length ? Promise.reject(new Error('Invalid email: ' + bad.join(', '))) : Promise.resolve();
                    }
                  }
                ]}
              >
                <Select
                  mode="tags"
                  placeholder="Add emails (press Enter to add)"
                  tokenSeparators={[',', ';']}
                  dropdownStyle={FORM_SELECT_DROPDOWN_STYLE}
                />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ marginBottom: 16, color: '#666', fontSize: '12px', marginLeft: 0 }}>
            When set, these users or emails are automatically added as followers to all tickets for this business.
          </div>

          <Form.Item
            name="address"
            label="Address"
            extra="Start typing for address suggestions, or enter manually."
          >
            <AddressAutocompleteInput
              placeholder="Enter street address"
              onPlaceResolved={(p) => {
                const { country, country_custom } = googleCountryToBusinessForm(p.countryShort);
                const cc = country === 'USA' ? 'US' : country === 'Canada' ? 'CA' : null;
                let stateVal = p.stateCode;
                if (cc) {
                  stateVal = findStateCodeByLabel(p.stateCode, cc);
                }
                form.setFieldsValue({
                  address: p.streetLine,
                  city: p.city,
                  state: stateVal,
                  zip_code: p.postalCode,
                  country,
                  country_custom,
                });
              }}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="city"
                label="City"
              >
                <Input placeholder="Enter city" />
              </Form.Item>
            </Col>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.country !== cur.country || prev.state !== cur.state}>
              {({ getFieldValue }) => {
                const country = getFieldValue('country');
                const state = getFieldValue('state');
                const usaCanada = country === 'USA' || country === 'Canada';
                const showSelect = usaCanada && shouldUseStateSelect(country, state);
                const code = country === 'USA' ? 'US' : 'CA';
                const options = getStatesByCountry(code).map((s) => ({
                  value: s.code,
                  label: `${s.name} (${s.code})`,
                }));
                return (
                  <Col span={8}>
                    {showSelect ? (
                      <Form.Item name="state" label="State / Province">
                        <Select
                          showSearch
                          allowClear
                          placeholder="Select state / province"
                          optionFilterProp="label"
                          options={options}
                          dropdownStyle={FORM_SELECT_DROPDOWN_STYLE}
                        />
                      </Form.Item>
                    ) : (
                      <Form.Item
                        name="state"
                        label="State / Province"
                        extra={
                          usaCanada
                            ? 'Value is not a standard US state or CA province — edit or pick country.'
                            : undefined
                        }
                      >
                        <Input placeholder="State / province" />
                      </Form.Item>
                    )}
                  </Col>
                );
              }}
            </Form.Item>
            <Col span={8}>
              <Form.Item
                name="zip_code"
                label="ZIP Code"
              >
                <Input placeholder="Enter ZIP code" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="country" label="Country" rules={[{ required: true, message: 'Please select a country' }]}>
            <Select
              placeholder="Select country"
              dropdownStyle={FORM_SELECT_DROPDOWN_STYLE}
              options={[
                { value: 'USA', label: 'United States' },
                { value: 'Canada', label: 'Canada' },
                { value: 'OTHER', label: 'Other' },
              ]}
              onChange={() => {
                form.setFieldsValue({ state: undefined });
              }}
            />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.country !== cur.country}>
            {({ getFieldValue }) =>
              getFieldValue('country') === 'OTHER' ? (
                <Form.Item
                  name="country_custom"
                  label="Country name"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter the country name',
                    },
                  ]}
                >
                  <Input placeholder="e.g. United Kingdom" />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item
            name="services"
            label="Services"
          >
            <Checkbox.Group options={serviceOptions} />
          </Form.Item>

          <Form.Item
            name="retailzpos_portal"
            label="Portal"
          >
            <Input placeholder="Enter Portal Number" />
          </Form.Item>

          {user?.role === 'admin' && (
            <Form.Item
              name="exclude_sla_notifications"
              label="Exclude SLA notifications"
              valuePropName="checked"
              extra="When enabled, SLA breach emails are not sent for tickets belonging to this business."
            >
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Card>

      {/* Equipment Assignment Section */}
      <Card title="Equipment Assignment" style={{ marginTop: 24 }}>
        <Form
          form={equipmentForm}
          layout="vertical"
          onFinish={addEquipment}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="equipment_id"
                label="Select Equipment"
                rules={[{ required: true, message: 'Please select equipment' }]}
              >
                <Select placeholder="Select equipment to assign">
                  {Array.isArray(availableEquipment) && availableEquipment.map((equipment: any) => (
                    <Option key={equipment.id} value={equipment.id}>
                      {EQUIPMENT_TYPES.find(t => t.value === equipment.equipment_type)?.label || equipment.equipment_type} - {equipment.model || 'No Model'}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="location"
                label="Location"
                rules={[{ required: true, message: 'Please enter location' }]}
              >
                <Input placeholder="Enter equipment location" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="notes"
                label="Notes"
              >
                <Input placeholder="Enter notes (optional)" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Add Equipment
            </Button>
          </Form.Item>
        </Form>

        {/* Equipment Assignment Table */}
        {equipmentToAssign.length > 0 && (
          <Table
            dataSource={equipmentToAssign}
            rowKey="id"
            pagination={false}
            size="small"
            columns={[
              {
                title: 'Type',
                dataIndex: 'equipment_type',
                key: 'equipment_type',
                render: (type: string) => (
                  <span>{EQUIPMENT_TYPES.find(t => t.value === type)?.label || type}</span>
                ),
              },
              {
                title: 'Model',
                dataIndex: 'model',
                key: 'model',
              },
              {
                title: 'Location',
                dataIndex: 'location',
                key: 'location',
              },
              {
                title: 'Notes',
                dataIndex: 'notes',
                key: 'notes',
              },
              {
                title: 'Actions',
                key: 'actions',
                render: (_, record, index) => (
                  <Popconfirm
                    title="Remove this equipment assignment?"
                    onConfirm={() => removeEquipment(index)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button type="text" danger size="small">
                      Remove
                    </Button>
                  </Popconfirm>
                ),
              },
            ]}
          />
        )}
      </Card>

      <Card style={{ marginTop: 24 }}>
        <Space>
          <Button type="primary" onClick={() => form.submit()} loading={createBusiness.isLoading}>
            {isEdit ? 'Update Business' : 'Create Business'}
          </Button>
          <Button onClick={() => navigate('/businesses')}>
            Cancel
          </Button>
        </Space>
      </Card>
    </div>
    );
  } catch (error) {
    console.error('CreateBusiness render error:', error);
    return (
      <div style={{ padding: 24 }}>
        <Alert 
          message="Error rendering form" 
          description={error instanceof Error ? error.message : 'Unknown rendering error'} 
          type="error" 
        />
        <Button onClick={() => navigate('/businesses')} style={{ marginTop: 16 }}>
          Back to Businesses
        </Button>
      </div>
    );
  }
};

export default CreateBusiness;
